import { useState, useCallback, useRef } from 'react';
import api from 'config/axiosConfig';
import { Issue, IssueSuggestion, WikiSuggestion, Suggestion } from 'util/constants';
import { getWikipediaSummary } from 'util/wikiUtils';
import { apiRoutes } from "util/apiRoutes";

/**
 * Re-export types for convenience
 */
export type { IssueSuggestion, WikiSuggestion, Suggestion };

/**
 * Configuration options for the issue search hook
 */
export interface UseIssueSearchOptions {
  /** Initial search query */
  initialQuery?: string;
  /** Array of issue IDs to exclude from results */
  excludeIssueIds?: number[];
  /** Whether to exclude compound issues (issues with a topic) */
  excludeCompoundIssues?: boolean;
  /** Initial limit for results on first search */
  initialResultsLimit?: number;
  /** Number of additional Wikipedia results to load on pagination */
  wikiPaginationStep?: number;
  /** Page size for backend issue results */
  backendPageSize?: number;
  /** Debounce delay in milliseconds */
  debounceDelay?: number;
}

/**
 * Return type for the useIssueSearch hook
 */
export interface UseIssueSearchReturn {
  /** Current search results */
  suggestions: Suggestion[];
  /** Whether the initial search is in progress */
  isSearching: boolean;
  /** Whether more results are being loaded */
  isLoadingMore: boolean;
  /** Whether there are more results available */
  hasMoreResults: boolean;
  /** Current search query */
  searchQuery: string;
  /** Perform a new search */
  performSearch: (query: string) => Promise<void>;
  /** Load more results for current search */
  loadMoreResults: () => Promise<void>;
  /** Clear all results and reset state */
  clearResults: () => void;
  /** Reset pagination state */
  resetPaginationState: () => void;
}

/**
 * Constants for pagination
 */
const DEFAULT_INITIAL_RESULTS_LIMIT = 30;
const DEFAULT_WIKI_PAGINATION_STEP = 20;
const DEFAULT_BACKEND_PAGE_SIZE = 10;

/**
 * Custom hook for searching issues with pagination support.
 * Searches both backend issues and Wikipedia, with automatic deduplication.
 * 
 * @param options Configuration options
 * @returns Search state and control functions
 */
export const useIssueSearch = (options: UseIssueSearchOptions = {}): UseIssueSearchReturn => {
  const {
    initialQuery = '',
    excludeIssueIds = [],
    excludeCompoundIssues = false,
    initialResultsLimit = DEFAULT_INITIAL_RESULTS_LIMIT,
    wikiPaginationStep = DEFAULT_WIKI_PAGINATION_STEP,
    backendPageSize = DEFAULT_BACKEND_PAGE_SIZE,
  } = options;

  // State
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMoreResults, setHasMoreResults] = useState(true);
  const [searchQuery, setSearchQuery] = useState(initialQuery);
  const [currentSearchTerm, setCurrentSearchTerm] = useState('');
  const [backendOffset, setBackendOffset] = useState(0);
  const [wikiLimit, setWikiLimit] = useState(0);

  // Track which Wikipedia titles we've already processed for the active search
  // term in a ref. State would be captured by closures inside `performSearchInternal`
  // and not be observable until the next render, which caused identical re-searches
  // (e.g. "church" -> "church " or backspace + retype) to filter out every result.
  const loadedWikiTitlesRef = useRef<Set<string>>(new Set());

  // Use ref to store abort controller for cancelling in-flight requests
  const abortControllerRef = useRef<AbortController | null>(null);

  /**
   * Reset pagination state to initial values
   */
  const resetPaginationState = useCallback(() => {
    setBackendOffset(0);
    setWikiLimit(0);
    loadedWikiTitlesRef.current = new Set();
    setHasMoreResults(true);
    setIsLoadingMore(false);
    setIsSearching(false);
  }, []);

  /**
   * Clear all results and reset state
   */
  const clearResults = useCallback(() => {
    setSuggestions([]);
    setSearchQuery('');
    setCurrentSearchTerm('');
    resetPaginationState();
  }, [resetPaginationState]);

  /**
   * Perform the actual search with pagination support
   */
  const performSearchInternal = useCallback(async (
    value: string,
    isInitialSearch: boolean = true,
    overrideOffset?: number
  ): Promise<void> => {
    if (!value) {
      setSuggestions([]);
      setIsSearching(false);
      return;
    }

    // Cancel any in-flight request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new abort controller for this request
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    if (isInitialSearch) {
      resetPaginationState();
      setCurrentSearchTerm(value);
      setIsSearching(true);
    }

    try {
      // 1. Fetch issue suggestions from backend
      const currentOffset = overrideOffset !== undefined ? overrideOffset : (isInitialSearch ? 0 : backendOffset);
      const backendResponse = await api.get(apiRoutes.issues.root, {
        signal: abortController.signal,
        params: {
          search: value,
          excludeIssueIds: `[${excludeIssueIds.join(",")}]`,
          excludeCompoundIssues,
          offset: currentOffset,
          limit: backendPageSize,
        },
      });

      // Check if request was aborted
      if (abortController.signal.aborted) {
        return;
      }

      const issueResults: Issue[] = backendResponse.data.filter((issue: Issue) =>
        !excludeIssueIds.some((excludeIssueId: number) => excludeIssueId === issue.id)
      );

      const issueSuggestions: IssueSuggestion[] = issueResults.map((issue) => ({
        ...issue,
        type: 'issue' as const
      }));

      // 2. Calculate how many Wikipedia results we need
      let remainingResults = 0;
      let currentWikiLimit = 0;

      if (isInitialSearch) {
        remainingResults = initialResultsLimit - issueSuggestions.length;
        currentWikiLimit = Math.max(0, remainingResults);
        setWikiLimit(currentWikiLimit);
      } else {
        // For pagination, increase wiki limit by step size
        currentWikiLimit = wikiLimit + wikiPaginationStep;
        setWikiLimit(currentWikiLimit);
      }

      // 3. Fetch from Wikipedia if we have remaining slots or if paginating
      let wikiSuggestions: Suggestion[] = [];
      if ((isInitialSearch && remainingResults > 0) || (!isInitialSearch && currentWikiLimit > 0)) {
        const wikiResponse = await fetch(
          `https://en.wikipedia.org/w/api.php?action=opensearch&search=${encodeURIComponent(
            value
          )}&limit=${currentWikiLimit}&namespace=0&format=json&origin=*`,
          { signal: abortController.signal }
        );

        // Check if request was aborted
        if (abortController.signal.aborted) {
          return;
        }

        const data = await wikiResponse.json();

        const existingCnames = new Set([
          ...issueResults.map(issue => issue.cname?.toLowerCase()).filter((c): c is string => !!c),
          ...(isInitialSearch ? [] : suggestions.filter((s): s is IssueSuggestion => s.type === 'issue' && !!s.cname).map(s => (s.cname || '').toLowerCase()))
        ]);

        // Filter out titles we've already loaded for the current search term.
        // Read from the ref so we always see the most recent value, even if a
        // sibling search call just reset it via `resetPaginationState`.
        const loadedWikiTitles = loadedWikiTitlesRef.current;
        const newWikiTitles = data[1].filter((title: string) =>
          !loadedWikiTitles.has(title.toLowerCase())
        );

        const allWiki = await Promise.all(
          newWikiTitles.map(async (title: string) => {
            try {
              const summaryData = await getWikipediaSummary(title);
              if (!summaryData) {
                return null;
              }
              const canonicalTitle = summaryData.canonicalTitle;

              if (existingCnames.has(canonicalTitle.toLowerCase())) {
                return null;
              }

              try {
                const existingIssueResponse = await api.get(apiRoutes.issues.byName(encodeURIComponent(canonicalTitle)));

                if (existingIssueResponse.status === 200 &&
                  !excludeIssueIds.some((excludeIssueId: number) => excludeIssueId === existingIssueResponse.data.id)) {
                  return {
                    type: 'issue' as const,
                    ...existingIssueResponse.data,
                    cname: canonicalTitle
                  };
                }
              } catch (error) {
                return {
                  type: 'wiki' as const,
                  name: summaryData.title,
                  link: summaryData.wikipediaUrl,
                  thumbnail: summaryData.thumbnail || '',
                  cname: canonicalTitle,
                  subject_properties: summaryData,
                  short_description: summaryData.description,
                };
              }
              return null;
            } catch (error) {
              console.error('Error fetching page summary:', error);
              return null;
            }
          })
        );

        wikiSuggestions = allWiki.filter((item): item is Suggestion => item !== null);

        // Update the live ref with the titles we just loaded so subsequent
        // pagination calls for this term skip them.
        newWikiTitles.forEach((title: string) => {
          loadedWikiTitlesRef.current.add(title.toLowerCase());
        });
      }

      // 4. Update suggestions based on whether this is initial search or pagination
      if (isInitialSearch) {
        const combinedSuggestions = [...issueSuggestions, ...wikiSuggestions];
        setSuggestions(combinedSuggestions as Suggestion[]);
      } else {
        // Append new results for pagination
        const newSuggestions = [...issueSuggestions, ...wikiSuggestions];
        setSuggestions(prev => [...prev, ...newSuggestions] as Suggestion[]);
      }

      // 5. Check if there are more results available
      const hasMoreBackendResults = issueResults.length === backendPageSize;
      const hasMoreWikiResults = wikiSuggestions.length > 0 || currentWikiLimit < 500; // Wikipedia max limit
      setHasMoreResults(hasMoreBackendResults || hasMoreWikiResults);

      // 6. Reset searching state for initial search
      if (isInitialSearch) {
        setIsSearching(false);
      }

    } catch (error: any) {
      // Ignore abort errors
      if (error.name === 'AbortError' || error.name === 'CanceledError') {
        return;
      }
      console.error('Error fetching search results:', error);
      if (isInitialSearch) {
        setIsSearching(false);
      }
    }
  }, [
    excludeIssueIds,
    excludeCompoundIssues,
    backendPageSize,
    initialResultsLimit,
    wikiPaginationStep,
    backendOffset,
    wikiLimit,
    suggestions,
    resetPaginationState
  ]);

  /**
   * Public function to perform a new search.
   *
   * The query is normalized (trimmed + collapsed whitespace) so that trailing
   * spaces or accidental double spaces don't change the request payload sent
   * to the backend or Wikipedia.
   */
  const performSearch = useCallback(async (query: string): Promise<void> => {
    const normalizedQuery = query.trim().replace(/\s+/g, ' ');
    setSearchQuery(normalizedQuery);
    await performSearchInternal(normalizedQuery, true);
  }, [performSearchInternal]);

  /**
   * Load more results for the current search
   */
  const loadMoreResults = useCallback(async (): Promise<void> => {
    if (isLoadingMore || !hasMoreResults || !currentSearchTerm) {
      return;
    }

    setIsLoadingMore(true);
    const newOffset = backendOffset + backendPageSize;
    setBackendOffset(newOffset);

    try {
      await performSearchInternal(currentSearchTerm, false, newOffset);
    } finally {
      setIsLoadingMore(false);
    }
  }, [
    isLoadingMore,
    hasMoreResults,
    currentSearchTerm,
    backendOffset,
    backendPageSize,
    performSearchInternal
  ]);

  return {
    suggestions,
    isSearching,
    isLoadingMore,
    hasMoreResults,
    searchQuery,
    performSearch,
    loadMoreResults,
    clearResults,
    resetPaginationState,
  };
};

